import { PastilleCodeNoyau } from '@/components/noyaux/pastille-code-noyau';
import { VoirAussiNoyaux } from '@/components/noyaux/voir-aussi-noyaux';
import type { OngletAnalyse } from '@/lib/params-recherche';
import { Icon } from '@pms-digital/design-system';
import { NOYAUX, type NoyauCode, type NoyauEnCours } from '@rosetta/shared';
import { Link } from '@tanstack/react-router';

interface EnteteAnalyseProps {
  id: string;
  /** The target noyau; unknown while the translation is being read. */
  noyau?: NoyauCode | undefined;
  /** The noyaux already translated ("Voir aussi" offers the others); empty while unknown. */
  traduits?: readonly NoyauCode[];
  onglet?: OngletAnalyse;
  /** The noyaux another launch of the document is translating (#36). */
  enCours?: readonly NoyauEnCours[];
}

/**
 * Header of the analysis: "Analyse des modifications", then "traduit vers" with the
 * pill and the long name of the target noyau (lower case: it sits in a sentence), and
 * "retour au résultat" on the right, towards the same noyau.
 */
export function EnteteAnalyse({
  id,
  noyau,
  traduits = [],
  onglet = 'avant-apres',
  enCours = [],
}: EnteteAnalyseProps) {
  return (
    <div className="flex items-start justify-between gap-6">
      <div className="flex flex-col gap-2">
        <h1 className="text-h2 leading-[1.2] tracking-normal">
          <span className="font-bold">Analyse</span>{' '}
          <span className="font-light">des modifications</span>
        </h1>
        {noyau === undefined ? null : (
          <p className="flex items-center gap-2 text-gray-700 text-small leading-[1.4]">
            <span>traduit vers</span>
            <PastilleCodeNoyau noyau={noyau} />
            <span className="text-black">
              <span className="font-bold">{NOYAUX[noyau].famille}</span>{' '}
              <span className="font-light">{NOYAUX[noyau].variante}</span>
            </span>
          </p>
        )}
        {noyau === undefined ? null : (
          <VoirAussiNoyaux
            libelle="Voir aussi les traductions vers :"
            idTitre="voir-aussi-titre"
            noyaux={traduits.filter((code) => code !== noyau)}
            enCours={enCours.filter((ligne) => ligne.noyau !== noyau)}
            lien={(code) => (
              <Link
                to="/resultat/$id/analyse"
                params={{ id }}
                search={{ noyau: code, onglet }}
                aria-label={`analyse de la traduction vers ${code}, ${NOYAUX[code].famille} ${NOYAUX[code].variante}`}
                className="inline-flex h-6 items-center rounded-full px-1 no-underline hover:bg-gray-100"
              >
                <PastilleCodeNoyau noyau={code} />
              </Link>
            )}
          />
        )}
      </div>
      <Link
        to="/resultat/$id"
        params={{ id }}
        search={noyau === undefined ? {} : { noyau }}
        className="inline-flex h-10 shrink-0 items-center gap-2 rounded-md px-3 font-semibold text-black text-small no-underline hover:bg-gray-100 hover:text-black"
      >
        <Icon name="arrow_back" size={20} />
        retour au résultat
      </Link>
    </div>
  );
}
